import { useState } from 'react';
import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Link,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import FormBase from '@/components/forms/new/FormBase';
import { Button } from '@/components/v4';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import { breadCrumbStyling } from 'components/forms/new/formStyles';
import { validationSchema } from './consts';
import walkthroughIds from './walkthroughIds';
import { updateServiceProvider, deleteServiceProvider } from './api';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import NameField from './formFields/NameField';
import OrganizationIdField from './formFields/OrganizationIdField';
import ApiKeyField from './formFields/ApiKeyField';
import KeyExpiryField from './formFields/KeyExpiryField';

const breadCrumbTrail = [
    { label: 'Service Providers', path: '/globaladmin/service-providers' },
    { label: 'Edit', path: '' },
];

export default function ServiceProvidersEdit({
    rowData,
    setServiceProvidersAction,
    setSuccessAction,
    requiredScope,
}) {
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const {
        isOpen: isDeleteModalOpen,
        onOpen: onDeleteModalOpen,
        onClose: onDeleteModalClose,
    } = useDisclosure();

    const { darkMode } = useSelector((state) => state.settings);
    const { createMode } = walkthroughIds;

    const initialValues = {
        name: rowData?.name || '',
        organizationId: rowData?.organizationId || '',
        apiKey: rowData?.apiKey || '',
        keyExpiry: rowData?.keyExpiry || '',
    };

    const finalFormSubmit = async (fields, actions) => {
        setIsSubmitting(true);
        setError(null);
        setSuccessAction(null);

        updateServiceProvider(rowData.id, fields, requiredScope)
            .then((response) => {
                if (response.status === 200) {
                    setSuccessAction('Edit');
                    setServiceProvidersAction('Get');
                    setTimeout(() => setSuccessAction(null), 6000);
                } else {
                    setError(
                        response.response?.data?.message ||
                            'Failed to update service provider.',
                    );
                }
            })
            .catch((err) => {
                setError(
                    err?.response?.data?.message ||
                        err?.data?.message ||
                        'Something went wrong when updating this service provider.',
                );
            })
            .finally(() => {
                setIsSubmitting(false);
                actions.setSubmitting(false);
            });
    };

    return (
        <>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        <BreadcrumbLink
                            as={Link}
                            to={crumb.path}
                            onClick={
                                index === 0
                                    ? () => setServiceProvidersAction('Get')
                                    : undefined
                            }>
                            {crumb.label}
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>
            <Text sx={{ fontSize: '20', fontWeight: '500' }}>
                Edit Service Provider
            </Text>
            <FormBase
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                validationSchema={validationSchema}
                ufBackBtn={createMode.ufBackBtn}
                ufSaveBtn={createMode.ufEditBtn}
                customClick={true}
                extraButton={
                    <Box w="100%" display="flex" justifyContent="flex-end">
                        <Button
                            w={142}
                            colorScheme="red"
                            onClick={onDeleteModalOpen}
                            isDisabled={isDeleting || isSubmitting}>
                            Delete
                        </Button>
                    </Box>
                }
                onBack={() => setServiceProvidersAction('Get')}>
                {({ errors = {}, touched = {} }) =>
                    [
                        <NameField
                            key="name"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <OrganizationIdField
                            key="organizationId"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <ApiKeyField
                            key="apiKey"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <KeyExpiryField
                            key="keyExpiry"
                            darkMode={darkMode}
                            isEditMode={true}
                        />,
                        error && (
                            <Alert
                                key="error"
                                status="error"
                                sx={{ margin: '25px 0' }}>
                                <AlertIcon />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        ),
                    ].filter(Boolean)
                }
            </FormBase>

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={onDeleteModalClose}
                serviceProviderName={rowData?.name}
                onConfirm={async () => {
                    try {
                        setIsDeleting(true);
                        await deleteServiceProvider(rowData.id, requiredScope);
                        setSuccessAction('Delete');
                        setServiceProvidersAction('Get');
                        setTimeout(() => setSuccessAction(null), 6000);
                    } catch (err) {
                        setError(
                            err?.response?.data?.message ||
                                err?.data?.message ||
                                'Failed to delete service provider.',
                        );
                    } finally {
                        setIsDeleting(false);
                        onDeleteModalClose();
                    }
                }}
                isDeleting={isDeleting}
            />
        </>
    );
}
